import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled, { css, keyframes } from "styled-components"
import {
  Chevron,
  DETAIL_COLUMN_ACTIVE_CLASSNAME,
  DetailColumn,
  DetailHeader,
  DetailsValue,
  ToContainer,
  ToHeader,
  ToPlaceholderText,
  ToValueText,
} from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/Shared/WorkItemRowSharedElements"

export const FIELD_PUSHED = "tr-field-accepted"
export const HIGHLIGHT_FIELD = "highlight-field"

const PUSH_FIELD_KEYFRAMES = keyframes`
  10%, 90% {
    border-color: ${colors.primary};
    box-shadow: 0 0 20px ${colors.translucentPrimary30};
  }
  0%, 100% {
    border-color: inherit;
    box-shadow: none;
  }
`

export const Header = styled(ToHeader)`
  color: ${colors.secondary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-weight: ${fonts.weight.medium};
  padding-left: 0;
  filter: unset;
  margin-top: 2px;

  &:hover {
    z-index: 2;
  }
`

export const StyledDetailColumn = styled(DetailColumn)`
  flex: unset;
  ${({ animateHeader }) =>
    !animateHeader &&
    css`
      ${DetailHeader} {
        position: absolute;
        top: 0;
        transform: translateY(calc(-100% - 5px));
        opacity: 1;
      }
    `};
`

export const DropdownIndicator = styled(Chevron)`
  opacity: 0.35;
  transition: opacity 100ms ease;
`

export const StyledPlaceholderText = styled(ToPlaceholderText)`
  color: ${colors.translucentSecondary60};
  transition: color 250ms ease;
  font-weight: ${fonts.weight.medium};
`

export const ToValue = styled(DetailsValue)`
  position: relative;
  color: ${colors.secondary};
  border-radius: 35px;
  padding: 6px 7px 6px 11px;
  user-select: none;
  height: 30px;
  cursor: pointer;

  &:hover,
  &.${DETAIL_COLUMN_ACTIVE_CLASSNAME} {
    ${StyledPlaceholderText} {
      color: ${colors.secondary};
    }

    ${DropdownIndicator} {
      opacity: 1;
    }
  }

  &.${HIGHLIGHT_FIELD} {
    outline: 3px solid ${colors.translucentPrimary30};
  }

  &.${FIELD_PUSHED} {
    outline: none;
    transition:
      border-color 100ms,
      box-shadow 200ms;
    animation: ${PUSH_FIELD_KEYFRAMES} 2s ease-in-out forwards;
  }
`

export const ClosedToValue = styled(DetailsValue)`
  position: relative;
  border: 0.5px solid ${colors.translucentSecondary40};
  border-radius: 35px;
  padding: 6px 7px 6px 11px;
  width: 200px;

  ${ToValueText} {
    color: ${colors.translucentSecondary60};
    font-weight: ${fonts.weight.normal};
    font-size: 12px;
  }
`

export const SmallClosedToValue = styled(DetailsValue)`
  position: relative;
  border: 0.5px solid ${colors.translucentSecondary40};
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const StyledToContainer = styled(ToContainer)<{
  isUserProvided: boolean
  hasSuggestion?: boolean
  hasError?: boolean
  small?: boolean
}>`
  font-size: 12px;
  transition: width 250ms ease-in;

  ${({ small }) =>
    small
      ? css`
          width: 30px;

          ${ToValue} {
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        `
      : css`
          width: 200px;
        `};

  ${({ hasSuggestion }) =>
    hasSuggestion &&
    css`
      ${ToValue} {
        max-width: 200px;
      }
    `}

  ${({ isUserProvided }) =>
    isUserProvided
      ? css`
          ${ToHeader} {
            filter: none;
          }

          ${DropdownIndicator} {
            ${svgPathStyles(colors.secondary, 1.5)};
          }

          ${ToValue} {
            border: 1px solid ${colors.translucentSecondary30};

            &:hover,
            &.${DETAIL_COLUMN_ACTIVE_CLASSNAME} {
              border-color: ${colors.secondary};
            }
          }
        `
      : css`
          ${ToValue} {
            color: ${colors.primary};
            border: 1px solid ${colors.translucentPrimary60};
          }

          ${DropdownIndicator} {
            ${svgPathStyles(colors.primary, 1.5)};
          }
        `}

  ${({ hasError }) =>
    hasError &&
    css`
      ${ToValue}.${DETAIL_COLUMN_ACTIVE_CLASSNAME}, ${ToValue} {
        &,
        &:hover,
        &.${DETAIL_COLUMN_ACTIVE_CLASSNAME} {
          border-color: ${colors.orange};
        }
      }
    `};
`
